import React, { FC, HTMLAttributes, memo, useCallback, useEffect, useRef } from 'react';
import DefaultLink from './Defaults/DefaultLink';
import DefaultTitle from './Defaults/DefaultTitle';
import SideMenuFactory from './Factory/SideMenuFactory';
import './SideMenu.css';

export type TCreateSideMenu = {
	title: string;
	link?: string;
	linkParams?: string;
	newTab?: boolean;
	next?: TCreateSideMenu | TCreateSideMenu[];
};

export interface ISideMenuProps extends HTMLAttributes<any> {
	TitleComponents?: FC[];
	LinkComponents?: FC[];
	open: boolean;
	setOpen: THandlerFunction;
	createSideMenu: TCreateSideMenu;
	freeze?: boolean;
	side?: 'left' | 'right';
	pageElement?: HTMLElement;
}

interface IMemoizedSideMenuContentProps {
	TitleComponents: ISideMenuProps['TitleComponents'];
	LinkComponents: ISideMenuProps['LinkComponents'];
	data: TCreateSideMenu;
}

const MemoizedSideMenuContent = memo(
	({
		data,
		TitleComponents = [DefaultTitle],
		LinkComponents = [DefaultLink],
	}: IMemoizedSideMenuContentProps) => {
		return (
			<>
				{SideMenuFactory(
					data,
					TitleComponents,
					LinkComponents,
					'SideMenu__link',
				)}
			</>
		);
	},
);

const SideMenu = ({
	open,
	createSideMenu,
	TitleComponents = [DefaultTitle],
	LinkComponents = [DefaultLink],
	setOpen,
	className,
	freeze = false,
	side = 'right',
	pageElement = undefined,
}: ISideMenuProps) => {
	const focusables = document.getElementsByClassName('SideMenu__link');
	const maxLeng = focusables.length - 1;
	const sideMenuRef = useRef<HTMLElement>(null);
	useEffect(() => {
		//Hide if SideMenu is inside an iframe
		if (sideMenuRef.current?.ownerDocument !== document) {
			sideMenuRef.current?.classList.add('hidden');
		} else {
			sideMenuRef.current?.classList.remove('hidden');
		}
	}, []);

	useEffect(() => {

		const body = document.querySelector('body') as HTMLElement;
		const moveSide = {
			left: 'SideMenu__move-right',
			right: 'SideMenu__move-left',
		}[side];

		if (body) {
			if (open) {
				body.classList.add(moveSide + '-body');
			} else {
				body.classList.remove('SideMenu__move-right-body');
				body.classList.remove('SideMenu__move-left-body');
			}
		}

		if (pageElement) {
			if (open) {
				pageElement.classList.add(moveSide + '-page');
			} else {
				pageElement.classList.remove('SideMenu__move-right-page');
				pageElement.classList.remove('SideMenu__move-left-page');
			}
		}

		if (open) {
			if (focusables.length) {
				(focusables[0] as HTMLElement).focus({ preventScroll: true });
			}
			const keyDownFunc = (e: KeyboardEvent) => {
				if (e.key === 'Escape') {
					setOpen(false);
				} else if (e.key === 'Tab') {
					if (e.shiftKey) {
						if (document.activeElement === focusables[0]) {
							setFocusedElement(maxLeng);
							e.preventDefault();
						}
					} else {
						if (document.activeElement === focusables[maxLeng]) {
							setFocusedElement(0);
							e.preventDefault();
						}
					}
				}
			};
			body.addEventListener('keydown', keyDownFunc);
			return () => body.removeEventListener('keydown', keyDownFunc);
		}
	}, [open])

	const setFocusedElement = useCallback((num: number) => {
		(focusables[num] as HTMLElement).focus({ preventScroll: true });
	}, []);

	return (
		<>
			<aside
				ref={sideMenuRef}
				className={`SideMenu min-h-full top-0 cursor-pointer absolute ${open
					? freeze
						? 'SideMenu__no-container__freeze visible'
						: 'SideMenu__no-container__open visible'
					: 'SideMenu__no-container__closed invisible hidden'
					} \
					${className || ''} \
					${side}-side
				`}
				onKeyDown={(e) => {
					if (e.key === 'Escape') {
						setOpen(false);
					}
				}}
				onClick={() => setOpen(false)}
			>
				<MemoizedSideMenuContent
					data={createSideMenu}
					TitleComponents={TitleComponents}
					LinkComponents={LinkComponents}
				/>
			</aside>
		</>
	);
};

export default SideMenu;
